import React, { useState } from "react";
import { motion } from "framer-motion";
import SectionHeader from "../components/SectionHeader";

const pricingPlans = [
  {
    name: "Starter",
    price: "$299",
    period: "month",
    description: "Perfect for small teams getting started with digital transformation",
    features: [
      "Cloud Infrastructure Setup",
      "Basic Data Pipeline",
      "5 User Accounts",
      "Email Support",
      "99.9% Uptime SLA"
    ],
    highlighted: false,
    cta: "Start Free Trial"
  },
  {
    name: "Professional",
    price: "$899",
    period: "month",
    description: "Ideal for growing businesses scaling their technology stack",
    features: [
      "Everything in Starter",
      "Advanced Cloud Architecture",
      "AI/ML Model Deployment",
      "25 User Accounts",
      "Priority Support",
      "Custom Integrations"
    ],
    highlighted: true,
    cta: "Most Popular"
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    description: "Tailored solutions for large organizations with complex needs",
    features: [
      "Everything in Professional",
      "Unlimited Users",
      "Dedicated Infrastructure",
      "Advanced Security & Compliance",
      "24/7 Phone Support",
      "Custom Development"
    ],
    highlighted: false,
    cta: "Contact Sales"
  }
];

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState("monthly");

  return (
    <section
      id="pricing"
      className="section-padding"
      style={{ background: "var(--navy2)" }}
      aria-labelledby="pricing-title"
    >
      <div className="max-w-[1200px] mx-auto">
        <SectionHeader
          tag="Pricing"
          title="Simple, Transparent<br/>Pricing Plans"
          subtitle="Choose the perfect plan for your business needs. All plans include our core platform features."
        />

        {/* Billing Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex justify-center mb-12"
        >
          <div className="inline-flex items-center bg-gray-800 rounded-lg p-1">
            <button
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                billingCycle === "monthly"
                  ? "bg-blue-600 text-white"
                  : "text-gray-400 hover:text-white"
              }`}
              onClick={() => setBillingCycle("monthly")}
            >
              Monthly
            </button>
            <button
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                billingCycle === "annual"
                  ? "bg-blue-600 text-white"
                  : "text-gray-400 hover:text-white"
              }`}
              onClick={() => setBillingCycle("annual")}
            >
              Annual (Save 20%)
            </button>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <motion.div 
          className="grid gap-8"
          style={{ gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))" }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {pricingPlans.map((plan, i) => (
            <motion.div
              key={plan.name}
              className={`relative rounded-2xl p-8 ${
                plan.highlighted
                  ? "border-2 border-blue-500 bg-gradient-to-b from-blue-600/20 to-transparent"
                  : "border border-gray-700 bg-gray-800/50"
              }`}
              style={{
                backdropFilter: "blur(10px)",
                background: plan.highlighted 
                  ? "linear-gradient(135deg, rgba(26,107,255,0.1), transparent)"
                  : "rgba(15,31,61,0.7)"
              }}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ 
                duration: 0.6, 
                delay: i * 0.1,
                type: "spring",
                stiffness: 100,
                damping: 15
              }}
              whileHover={{ 
                y: -8,
                scale: 1.02,
                transition: { duration: 0.3, type: "spring", stiffness: 400 }
              }}
            >
              {/* Popular Badge */}
              {plan.highlighted && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.8 }}
                  className="absolute -top-4 left-1/2 transform -translate-x-1/2"
                >
                  <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </span>
                </motion.div>
              )}

              <h3
                className="font-display text-white mb-2 text-xl tracking-tight"
                style={{ letterSpacing: "-0.025em" }}
              >
                {plan.name}
              </h3>
              
              <div className="mb-4">
                {plan.price !== "Custom" ? (
                  <>
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                    <span className="text-gray-400">/{plan.period}</span>
                  </>
                ) : (
                  <span className="text-2xl font-bold text-white">{plan.price}</span>
                )}
              </div>

              <p className="text-sm text-gray-400 mb-6 leading-relaxed">
                {plan.description}
              </p>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <motion.li
                    key={feature}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ 
                      duration: 0.4, 
                      delay: 0.6 + i * 0.05 + index * 0.1,
                      type: "spring",
                      stiffness: 100
                    }}
                    className="flex items-start gap-3 text-sm text-gray-300"
                  >
                    <span
                      className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ minWidth: "20px" }}
                    >
                      <span className="text-green-400 text-xs">✓</span>
                    </span>
                    {feature}
                  </motion.li>
                ))}
              </ul>

              <button
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-all ${
                  plan.highlighted
                    ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800"
                    : "bg-gray-700 text-white hover:bg-gray-600"
                }`}
                onClick={() => {
                  if (plan.price === "Custom") {
                    // Navigate to contact
                    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                  } else {
                    // Handle trial/signup
                    console.log(`Starting ${plan.name} plan trial`);
                  }
                }}
              >
                {plan.cta}
              </button>
            </motion.div>
          ))}
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="flex justify-center gap-8 mt-16 flex-wrap"
        >
          {[
            { icon: "🔒", text: "Secure Payment Processing" },
            { icon: "↩️", text: "30-Day Money Back Guarantee" },
            { icon: "🚀", text: "Instant Setup" }
          ].map((badge, i) => (
            <motion.div
              key={badge.text}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ 
                duration: 0.5, 
                delay: 0.9 + i * 0.1,
                type: "spring",
                stiffness: 100
              }}
              className="flex items-center gap-2 text-sm text-gray-400"
            >
              <span className="text-xl">{badge.icon}</span>
              <span>{badge.text}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
